import { createContext, useCallback, useContext, useState } from 'react'
import { Toast, ToastContainer } from 'react-bootstrap'

export type ToastVariant = 'success' | 'danger' | 'warning' | 'info'

interface ToastMessage {
  id: number
  title?: string
  message: string
  variant: ToastVariant
}

interface ToastContextValue {
  showToast: (message: string, options?: { title?: string; variant?: ToastVariant }) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return ctx
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([])

  const showToast = useCallback(
    (message: string, options?: { title?: string; variant?: ToastVariant }) => {
      const id = Date.now()
      setMessages((prev) => [
        ...prev,
        {
          id,
          message,
          title: options?.title,
          variant: options?.variant ?? 'info',
        },
      ])
    },
    []
  )

  const handleClose = (id: number) => {
    setMessages((prev) => prev.filter((m) => m.id !== id))
  }

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer position="top-end" className="p-3">
        {messages.map((toast) => (
          <Toast
            key={toast.id}
            bg={
              toast.variant === 'success'
                ? 'success'
                : toast.variant === 'danger'
                ? 'danger'
                : toast.variant === 'warning'
                ? 'warning'
                : 'info'
            }
            onClose={() => handleClose(toast.id)}
            delay={5000}
            autohide
          >
            {toast.title && (
              <Toast.Header closeButton={false}>
                <strong className="me-auto">{toast.title}</strong>
              </Toast.Header>
            )}
            <Toast.Body className={toast.variant === 'warning' || toast.variant === 'info' ? 'text-dark' : 'text-white'}>
              {toast.message}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  )
}

